import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, KeyRound, RotateCcw, Ban, CheckCircle2, Lock, Link2, Copy } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const EDIT_ROLES = [
  { key: "admin_ops", label: "Admin Ops" },
  { key: "kewag_admin", label: "KEWAG Admin" },
  { key: "moderator", label: "Moderator" },
  { key: "support", label: "Support" },
];

const ADMIN_OPS_MATRIX = [
  ["Kunden / Brockis", "Lesen + Bearbeiten", "ok"],
  ["Standorte", "Lesen + Bearbeiten", "ok"],
  ["Services", "Lesen + Bearbeiten", "ok"],
  ["Aktionen & News", "Lesen + Bearbeiten", "ok"],
  ["Fundstücke", "Lesen + Bearbeiten", "ok"],
  ["Medien", "Lesen + Bearbeiten", "ok"],
  ["Anfragen", "Lesen + Bearbeiten", "ok"],
  ["Bewertungen", "Lesen + Moderieren", "ok"],
  ["Onboarding", "Lesen + Bearbeiten", "ok"],
  ["Support", "Lesen + Bearbeiten", "ok"],
  ["Kunden-Cockpit", "READ ONLY", "ro"],
  ["Billing", "READ ONLY", "ro"],
  ["Owner / Super-Admin", "KEIN ZUGRIFF", "no"],
  ["Security Core", "KEIN ZUGRIFF", "no"],
  ["R2 / Cutover", "KEIN ZUGRIFF", "no"],
  ["Secrets", "KEIN ZUGRIFF", "no"],
];
const SUPER_MATRIX = ADMIN_OPS_MATRIX.map(([m]) => [m, "Voller Zugriff", "ok"]);
const TAG_CLS = {
  ok: "border-status-success-border bg-status-success-bg text-status-success",
  ro: "border-status-warning-border bg-status-warning-bg text-status-warning",
  no: "border-status-danger-border bg-status-danger-bg text-status-danger",
};

function Row({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-fundio-border py-2.5">
      <span className="text-xs uppercase tracking-wide text-fundio-muted">{label}</span>
      <span data-testid={testid} className="text-right text-sm font-medium text-fundio-ink">{value}</span>
    </div>
  );
}

export function UserDetailDrawer({ userId, open, onOpenChange, onChanged }) {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";
  const [d, setD] = useState(null);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "", status: "active" });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, desc, danger, run }
  const [pw, setPw] = useState({ current: "", next: "" });
  const [resetLink, setResetLink] = useState(null); // { url, expires_at } — shown once

  const load = useCallback(async () => {
    if (!userId) return;
    setD(null);
    setResetLink(null);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setD(data);
      setForm({ name: data.name || "", email: data.email || "", role: data.role, status: data.status || "active" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    try { const a = await api.get(`/users/${userId}/audit`); setAudit(a.data || []); } catch { setAudit([]); }
  }, [userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = async () => { await load(); onChanged?.(); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${userId}`, { name: form.name, email: form.email, role: form.role, status: form.status });
      toast.success("Benutzer aktualisiert");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = (title, desc, run, danger = false) => setConfirm({ title, desc, run, danger });

  const runAction = async () => {
    const c = confirm; setConfirm(null);
    try { await c.run(); await refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeOwnPw = async () => {
    try {
      await api.post(`/auth/change-my-password`, { current_password: pw.current, new_password: pw.next });
      toast.success("Passwort geändert");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const genResetLink = async () => {
    try {
      const { data } = await api.post(`/users/${userId}/reset-link`);
      setResetLink({ url: data.url, expires_at: data.expires_at });
      toast.success("Einmal-Link erzeugt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.url);
      toast.success("Link kopiert");
    } catch { toast.error("Kopieren nicht möglich — bitte manuell markieren."); }
  };

  const matrix = d?.is_owner ? SUPER_MATRIX : d?.role === "admin_ops" ? ADMIN_OPS_MATRIX
    : Object.entries(d?.permissions || {}).filter(([, v]) => v.read).map(([m, v]) => [m, v.write ? "Lesen + Bearbeiten" : "Lesen", "ok"]);

  const yesno = (b) => (b ? "Ja" : "Nein");
  const canManage = isSuper && d && !d.is_owner;
  const selfOwner = d && d.is_owner && d.id === me?.id;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent data-testid="user-detail-drawer" className="w-full overflow-y-auto border-fundio-border bg-fundio-bg sm:max-w-xl">
          {!d ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-fundio-ink">
                  <span data-testid="drawer-user-name">{d.name}</span>
                  {d.is_owner ? (
                    <span data-testid="drawer-owner-badge" className="ml-2 rounded-md border border-fundio-gold bg-fundio-gold/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-fundio-gold">Super Admin / Owner</span>
                  ) : d.role === "admin_ops" ? (
                    <span className="ml-2 rounded-md border border-status-info-border bg-status-info-bg px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-status-info">Admin Ops</span>
                  ) : null}
                </SheetTitle>
                {d.is_protected_owner && (
                  <p data-testid="drawer-owner-protected" className="flex items-center gap-1.5 text-xs font-medium text-fundio-gold"><Lock className="h-3.5 w-3.5" /> Geschützter Owner — einziger Super-Admin</p>
                )}
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="flex w-full flex-wrap gap-1 bg-fundio-subtle">
                  <TabsTrigger data-testid="tab-overview" value="overview">Übersicht</TabsTrigger>
                  {(canManage || selfOwner) && <TabsTrigger data-testid="tab-edit" value="edit">Bearbeiten</TabsTrigger>}
                  {(canManage || selfOwner) && <TabsTrigger data-testid="tab-security" value="security">Sicherheit</TabsTrigger>}
                  <TabsTrigger data-testid="tab-permissions" value="permissions">Berechtigungen</TabsTrigger>
                  <TabsTrigger data-testid="tab-audit" value="audit">Audit</TabsTrigger>
                </TabsList>

                {/* OVERVIEW */}
                <TabsContent value="overview" className="mt-3">
                  <Row label="Name" value={d.name} testid="detail-name" />
                  <Row label="E-Mail" value={d.email} testid="detail-email" />
                  <Row label="Benutzer-ID" value={<span className="font-mono text-xs">{d.id}</span>} testid="detail-id" />
                  <Row label="Rolle" value={d.is_owner ? "Super Admin / Owner" : d.role_label} testid="detail-role" />
                  <Row label="Status" value={d.status === "active" ? "Aktiv" : "Inaktiv"} testid="detail-status" />
                  <Row label="2FA aktiv" value={yesno(d.two_factor_enabled)} testid="detail-2fa" />
                  <Row label="Passwortwechsel erforderlich" value={yesno(d.must_change_password)} testid="detail-mcp" />
                  <Row label="Letzte Anmeldung" value={d.last_login ? formatDate(d.last_login) : "—"} testid="detail-last-login" />
                  <Row label="Erstellt am" value={d.created_at ? formatDate(d.created_at) : "—"} testid="detail-created" />
                  <Row label="Letzte Rollenänderung" value={d.last_role_change ? formatDate(d.last_role_change) : "—"} testid="detail-role-change" />
                </TabsContent>

                {/* EDIT */}
                {(canManage || selfOwner) && (
                  <TabsContent value="edit" className="mt-3 space-y-4">
                    <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Name</Label>
                      <Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" /></div>
                    <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">E-Mail</Label>
                      <Input data-testid="edit-email" type="email" value={form.email} disabled={d.is_owner} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark disabled:opacity-60" />
                      {d.is_owner && <p className="text-[11px] text-fundio-gold">Owner-E-Mail ist geschützt.</p>}</div>
                    <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Rolle</Label>
                      {d.is_owner ? (
                        <div data-testid="edit-role-locked" className="flex items-center gap-2 rounded-md border border-fundio-gold bg-fundio-gold/10 px-3 py-2 text-sm font-medium text-fundio-gold"><Lock className="h-4 w-4" /> Super Admin / Owner — nicht änderbar (einziger Super-Admin)</div>
                      ) : (
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                          <SelectTrigger data-testid="edit-role-select" className="input-dark"><SelectValue /></SelectTrigger>
                          <SelectContent>{EDIT_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}</div>
                    <div className="space-y-1.5"><Label className="text-xs text-fundio-muted">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={d.is_protected_owner}>
                        <SelectTrigger data-testid="edit-status-select" className="input-dark disabled:opacity-60"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="active">Aktiv</SelectItem><SelectItem value="inactive">Inaktiv</SelectItem></SelectContent>
                      </Select></div>
                    <Button data-testid="edit-save-button" disabled={saving || !form.name} onClick={saveEdit} className="w-full border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream hover:border-fundio-gold-highlight">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Änderungen speichern"}</Button>
                  </TabsContent>
                )}

                {/* SECURITY */}
                {(canManage || selfOwner) && (
                  <TabsContent value="security" className="mt-3 space-y-3">
                    {canManage ? (
                      <>
                        <SecBtn testid="security-reset-link-btn" icon={Link2} label="Einmal-Link für Passwort erzeugen" hint="Persönlicher, zeitlich begrenzter Link zum selbst Setzen des Passworts (einmalig gültig). Für sichere, persönliche Übergabe."
                          onClick={genResetLink} />
                        {resetLink && (
                          <div data-testid="reset-link-box" className="space-y-2 rounded-md border border-fundio-gold bg-fundio-gold/10 p-3">
                            <p className="text-xs font-semibold text-fundio-gold">Einmal-Link erzeugt — wird nur jetzt angezeigt</p>
                            <code data-testid="reset-link-url" className="block select-all break-all rounded bg-fundio-dark2/60 p-2 font-mono text-[11px] text-fundio-ink">{resetLink.url}</code>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] text-fundio-muted">Gültig bis {formatDate(resetLink.expires_at)}</span>
                              <Button data-testid="reset-link-copy" size="sm" variant="outline" onClick={copyResetLink} className="h-7 gap-1.5 text-xs"><Copy className="h-3 w-3" /> Kopieren</Button>
                            </div>
                            <p className="text-[11px] text-fundio-muted">Übergeben Sie diesen Link ausschliesslich persönlich/sicher — nicht per E-Mail oder Chat. Nach dem Setzen des Passworts folgt beim Login die 2FA-Einrichtung.</p>
                          </div>
                        )}
                        <SecBtn testid="security-reset-password-btn" icon={KeyRound} label="Passwort zurücksetzen" hint="Neues sicheres Temp-Passwort (Secret-Store), Wechsel erzwungen, Sessions widerrufen"
                          onClick={() => act("Passwort zurücksetzen?", `Ein neues sicheres Temp-Passwort für ${d.email} wird im Secret-Store hinterlegt (nie angezeigt). Alle Sessions werden widerrufen und ein Wechsel beim nächsten Login erzwungen.`, async () => { const r = await api.post(`/users/${userId}/reset-password`); toast.success(`Temp-Passwort im Secret-Store hinterlegt (${r.data.secret_store_key}).`); })} />
                        <SecBtn testid="security-force-change-btn" icon={RotateCcw} label="Passwortwechsel erzwingen" hint="must_change_password aktivieren, Sessions widerrufen"
                          onClick={() => act("Passwortwechsel erzwingen?", `${d.email} muss beim nächsten Login das Passwort ändern. Sessions werden widerrufen.`, async () => { await api.post(`/users/${userId}/force-password-change`); toast.success("Passwortwechsel erzwungen"); })} />
                        <SecBtn testid="security-reset-2fa-btn" icon={ShieldOff} label="2FA zurücksetzen" hint="TOTP entfernen, Neu-Einrichtung beim nächsten Login erzwingen"
                          onClick={() => act("2FA zurücksetzen?", `Die 2FA von ${d.email} wird entfernt. Beim nächsten Login ist eine erneute 2FA-Einrichtung zwingend. Sessions werden widerrufen.`, async () => { await api.post(`/users/${userId}/reset-2fa`); toast.success("2FA zurückgesetzt"); })} />
                        <SecBtn testid="security-revoke-sessions-btn" icon={Ban} label="Sessions widerrufen" hint="Alle aktiven Sitzungen sofort beenden"
                          onClick={() => act("Sessions widerrufen?", `Alle aktiven Sitzungen von ${d.email} werden sofort beendet.`, async () => { await api.post(`/users/${userId}/revoke-sessions`); toast.success("Sessions widerrufen"); })} />
                        <SecBtn testid="security-toggle-status-btn" icon={d.status === "active" ? Ban : CheckCircle2} label={d.status === "active" ? "Konto deaktivieren" : "Konto aktivieren"} hint={d.status === "active" ? "Login sofort sperren" : "Login wieder erlauben"} danger={d.status === "active"}
                          onClick={() => act(d.status === "active" ? "Konto deaktivieren?" : "Konto aktivieren?", `Der Kontostatus von ${d.email} wird auf ${d.status === "active" ? "INAKTIV" : "AKTIV"} gesetzt.`, async () => { await api.patch(`/users/${userId}`, { status: d.status === "active" ? "inactive" : "active" }); toast.success("Status aktualisiert"); }, d.status === "active")} />
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="rounded-md border border-fundio-gold bg-fundio-gold/10 p-3 text-xs text-fundio-gold"><Lock className="mr-1 inline h-3.5 w-3.5" /> Geschützter Owner — eigene Sicherheitseinstellungen.</div>
                        <div className="space-y-2 rounded-md border border-fundio-border p-3">
                          <p className="text-sm font-semibold text-fundio-ink">Eigenes Passwort ändern</p>
                          <Input data-testid="owner-current-pw" type="password" placeholder="Aktuelles Passwort" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="input-dark" />
                          <Input data-testid="owner-new-pw" type="password" placeholder="Neues Passwort (min. 10 Zeichen)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="input-dark" />
                          <Button data-testid="owner-change-pw-btn" disabled={!pw.current || pw.next.length < 10} onClick={changeOwnPw} className="w-full border border-fundio-gold bg-fundio-dark2 font-semibold text-fundio-cream">Passwort ändern</Button>
                        </div>
                        <SecBtn testid="owner-revoke-sessions-btn" icon={Ban} label="Eigene Sessions widerrufen" hint="Meldet dich auf allen Geräten ab" danger
                          onClick={() => act("Eigene Sessions widerrufen?", "Alle deine aktiven Sitzungen werden beendet. Du wirst abgemeldet.", async () => { await api.post(`/users/${userId}/revoke-sessions`); toast.success("Sessions widerrufen"); }, true)} />
                        <Row label="2FA aktiv" value={yesno(d.two_factor_enabled)} testid="owner-2fa-status" />
                      </div>
                    )}
                  </TabsContent>
                )}

                {/* PERMISSIONS */}
                <TabsContent value="permissions" className="mt-3">
                  <table data-testid="permissions-matrix" className="w-full text-sm">
                    <tbody className="divide-y divide-fundio-border">
                      {matrix.map(([mod, access, tag]) => (
                        <tr key={mod}>
                          <td className="py-2.5 pr-3 font-medium text-fundio-ink">{mod}</td>
                          <td className="py-2.5 text-right"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TAG_CLS[tag]}`}>{access}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TabsContent>

                {/* AUDIT */}
                <TabsContent value="audit" className="mt-3">
                  {audit.length === 0 ? (
                    <p data-testid="audit-empty" className="py-6 text-center text-sm text-fundio-muted">Keine Ereignisse.</p>
                  ) : (
                    <ul data-testid="user-audit-list" className="space-y-2">
                      {audit.map((a) => (
                        <li key={a.id} className="rounded-md border border-fundio-border bg-white p-2.5 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold uppercase tracking-wide text-fundio-ink">{a.action}</span>
                            <span className="text-fundio-faint">{a.timestamp ? formatDate(a.timestamp) : ""}</span>
                          </div>
                          <p className="mt-1 text-fundio-muted">{a.detail}</p>
                          <p className="mt-0.5 text-[11px] text-fundio-faint">von {a.user_email}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="user-action-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-title">{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ok" onClick={runAction} className={confirm?.danger ? "bg-status-danger text-white hover:bg-status-danger/90" : ""}>Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SecBtn({ testid, icon: Icon, label, hint, onClick, danger }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors ${danger ? "border-status-danger-border hover:bg-status-danger-bg/50" : "border-fundio-border hover:border-fundio-gold hover:bg-fundio-subtle"}`}>
      <Icon className={`mt-0.5 h-4 w-4 ${danger ? "text-status-danger" : "text-fundio-gold"}`} />
      <span><span className="block text-sm font-semibold text-fundio-ink">{label}</span><span className="block text-xs text-fundio-muted">{hint}</span></span>
    </button>
  );
}
